import { isApiError, unwrap } from "@sagot/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { inboxKeys } from "./inbox-api";
import { OFFER_POLL_MS } from "./offers-model";
import { isLiveChatOff, presenceKeys } from "./use-presence";

export const offerKeys = {
  mine: (ws: string) => ["ws", ws, "routing", "offers", "mine"] as const,
  settings: (ws: string) => ["ws", ws, "routing", "offers", "settings"] as const,
};

/**
 * `GET /v1/routing/offers/me`: the chats offered to the member that they can still answer. `offsetMs` is the API's clock
 * minus the console's at the moment of the answer, so the countdown agrees with the server whatever the local clock says.
 * A 404 is the answer "live chat is off here".
 */
export function useMyOffers(enabled: boolean) {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: offerKeys.mine(ws),
    queryFn: async () => {
      const answer = await unwrap(api.GET("/v1/routing/offers/me"));
      return { offers: answer.offers, offsetMs: Date.parse(answer.server_time) - Date.now() };
    },
    enabled,
    retry: false,
    staleTime: 0,
    // a workspace without live chat answers 404 once: no point asking every ten seconds
    refetchInterval: (query) => (isLiveChatOff(query.state.error) ? false : OFFER_POLL_MS),
  });
}

type Answer = "accept" | "decline";

/** `POST /v1/conversations/{id}/offer/accept|decline`. */
export function useAnswerOffer() {
  const api = useApi();
  const ws = useWorkspaceId();
  const queryClient = useQueryClient();
  /** An answered (or gone) offer leaves the toast at once; the refetch after it only confirms. */
  const dropOffer = (conversationId: string) => {
    queryClient.setQueryData(offerKeys.mine(ws), (old: { offers: { conversation_id: string }[]; offsetMs: number } | undefined) =>
      old ? { ...old, offers: old.offers.filter((offer) => offer.conversation_id !== conversationId) } : old,
    );
  };
  return useMutation({
    mutationFn: ({ conversationId, answer }: { conversationId: string; answer: Answer }) => {
      const path = { conversation_id: conversationId };
      return answer === "accept"
        ? unwrap(api.POST("/v1/conversations/{conversation_id}/offer/accept", { params: { path } }))
        : unwrap(api.POST("/v1/conversations/{conversation_id}/offer/decline", { params: { path } }));
    },
    onSuccess: (_answered, { conversationId }) => { dropOffer(conversationId); },
    onError: (error, { conversationId }) => {
      if (isApiError(error) && error.code === "offer_expired") dropOffer(conversationId);
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: offerKeys.mine(ws) }),
        queryClient.invalidateQueries({ queryKey: inboxKeys.root(ws) }),
        queryClient.invalidateQueries({ queryKey: presenceKeys.me(ws) }),
        queryClient.invalidateQueries({ queryKey: presenceKeys.list(ws) }),
      ]);
    },
  });
}

/** `GET /v1/routing/offer-settings`: whether chats are offered and for how many seconds. Asked only once the rollout probe said live chat is on. */
export function useOfferSettings(enabled: boolean) {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: offerKeys.settings(ws),
    queryFn: () => unwrap(api.GET("/v1/routing/offer-settings")),
    enabled,
    retry: false,
    staleTime: 15_000,
  });
}

/** `PUT /v1/routing/offer-settings`. */
export function useSaveOfferSettings() {
  const api = useApi();
  const ws = useWorkspaceId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { enabled: boolean; timeout_s: number }) => unwrap(api.PUT("/v1/routing/offer-settings", { body })),
    onSuccess: (saved) => {
      queryClient.setQueryData(offerKeys.settings(ws), saved);
    },
  });
}
